import * as S from '@effect/schema/Schema'
import { nonZeroNullable } from '@mathflat/shared/@common/schema/combinators'
import {
  ISODate,
  ISODateString,
  NonZeroNullableNumber,
} from '@mathflat/shared/@common/schema/schema'
import type { ValueOf } from '@mathflat/shared/@types/utilityTypes'

import { AcademyDomain } from '~/@entities/Academy/module.ts'
import { CURRICULUM_CONCEPT_TYPE } from '~/@entities/Curriculum/constant'
import { allGradeSubject, schoolType, semester } from '~/@entities/SchoolSystem/schema'
import { StudentDomain } from '~/@entities/Student/domain.ts'
import { TeacherSchema } from '~/@entities/Teacher/schema'

import { ContentLevel } from '../module'

/**
 * *minPage, maxPage, answerPage 페이지의 경우
 * min max 페이지가 있다는 것은 인쇄할 수 있다는 의미이기도 하다.(저작권 문제 없는)
 * 근데 원본 시그니쳐 교재에서는 다른 의미로 사용된다.
 *
 * *서버에서 내려주는 defaultPageUrl, defaultAnswerPdfUrl의 경우
 * 서버에서 pdf를 만드는데 필요한 정보..? 같은 거라 프론트에서 알 필요가 없다.
 */

export module WorkbookDomain {
  export const TYPE = {
    시중교재: 'PUBLIC',
    교과서: 'SCHOOL',
    내교재: 'CUSTOM',
    커스텀시그니처교재: 'CUSTOM_SIGNATURE',
    시그니처교재: 'SIGNATURE',
    학원그룹교재: 'ACADEMY_GROUP',
  } as const

  export const ACADEMY_GROUP_TYPE = {
    WEIZMANN: 'WEIZMANN',
    CREVERSE: 'CREVERSE',
  } as const

  export type AcademyGroupType = (typeof ACADEMY_GROUP_TYPE)[keyof typeof ACADEMY_GROUP_TYPE]

  /**
   * 학원그룹교재는 시그니처교재의 변형이 아님.
   * 북마크 비활성화, 비디오 트래킹 등 동작이 같을 뿐 별개 개념.
   */
  export function isSignatureOrAcademyGroupWorkbook(type: Type): boolean {
    return (
      type === TYPE.시그니처교재 || type === TYPE.커스텀시그니처교재 || type === TYPE.학원그룹교재
    )
  }

  export const WORKBOOK_TYPE_LIST = [
    { label: '내교재', value: TYPE.내교재 },
    { label: '시그니처교재', value: TYPE.커스텀시그니처교재 },
    { label: '학원그룹교재', value: TYPE.학원그룹교재 },
    { label: '시중교재', value: TYPE.시중교재 },
    { label: '교과서', value: TYPE.교과서 },
  ] as const

  export const SIGNATURE_TYPES = [
    'ARITHMETIC',
    'BASIC',
    'CONCEPT',
    'HIGH',
    'SCHOOL_TEST',
    'VACATION_BOOTCAMP',
    'CONCEPT_UNIT_LIGHT',
    'N_PROBLEM',
  ] as const

  const CUSTOM_SIGNATURE_FEATURE_TYPES = ['SAMPLE', 'RENT', 'OWN', 'DRAFT', 'COVER_SAMPLE'] as const

  /**
   * [시중교재]
   * 1군: 지원해야하는 시중교재 콘팀이 판단 → 시중교재 구매 > 빠른정답 입력 > CMS 업로드
   * 2군: 교재 수급 → 스캔대행 → OCR 분석 → 유사문제 추출 → DB 등록` (≒ 정답이 없다.)
   * [내신기출 원본]
   * 1군: 해당 학교의 실제 시험지 원본
   * 2군: 해당 학교의 시험범위를 파악해 콘팀에서 유사하게 만든 시험지
   */
  export const SECURING_TYPE = {
    PRIMARY: 'PRIMARY', // 1군
    SECONDARY: 'SECONDARY', // 2군
  } as const

  export type SecuringType = (typeof SECURING_TYPE)[keyof typeof SECURING_TYPE]

  export const ASSIGNABLE_CUSTOM_SIGNATURE_FEATURE_TYPES = CUSTOM_SIGNATURE_FEATURE_TYPES.filter(
    (type) => type !== 'COVER_SAMPLE' && type !== 'DRAFT',
  )

  export type SignatureType = (typeof SIGNATURE_TYPES)[number]

  export type CustomSignatureFeatureType = (typeof CUSTOM_SIGNATURE_FEATURE_TYPES)[number]

  export const SIGNATURE_TYPE = {
    연산: 'ARITHMETIC',
    유형서: 'BASIC',
    개념서: 'CONCEPT',
    심화유형서: 'HIGH',
    내신대비: 'SCHOOL_TEST',
    방학특강: 'VACATION_BOOTCAMP',
    개념유형라이트: 'CONCEPT_UNIT_LIGHT',
    N제: 'N_PROBLEM',
  } as const

  export const SIGNATURE_TYPE_KO: Record<
    SignatureType,
    '연산' | '유형서' | '개념서' | '심화유형서' | '내신대비' | '방학특강' | '개념유형라이트' | 'N제'
  > = {
    ARITHMETIC: '연산',
    BASIC: '유형서',
    CONCEPT: '개념서',
    HIGH: '심화유형서',
    SCHOOL_TEST: '내신대비',
    VACATION_BOOTCAMP: '방학특강',
    CONCEPT_UNIT_LIGHT: '개념유형라이트',
    N_PROBLEM: 'N제',
  } as const

  export const CUSTOM_SIGNATURE_FEATURE_TYPE: Record<
    '체험교재' | '대여교재' | '구매교재' | '표지편집_전_교재' | '표지_편집용_체험교재',
    CustomSignatureFeatureType
  > = {
    체험교재: 'SAMPLE',
    대여교재: 'RENT',
    구매교재: 'OWN',
    표지편집_전_교재: 'DRAFT',
    표지_편집용_체험교재: 'COVER_SAMPLE',
  } as const

  export const ALLOWED_CUSTOM_SIGNATURE_FEATURE_TYPE_BY_PERMISSION = {
    PREMIUM: Object.values(CUSTOM_SIGNATURE_FEATURE_TYPE),
    BASIC: [CUSTOM_SIGNATURE_FEATURE_TYPE.체험교재, CUSTOM_SIGNATURE_FEATURE_TYPE.구매교재],
  }

  export const FILTER_TYPES = [
    'CONCEPT',
    'CORNER',
    'CONCEPT_BY_WORKBOOK',
    'CORNER_BY_WORKBOOK',
    'SCHOOL_TEST',
  ] as const

  export type FilterType = (typeof FILTER_TYPES)[number]
  export type TYPE = typeof TYPE

  export const Id = S.number
  export const Type = S.enums(TYPE)
  export type Type = S.Schema.To<typeof Type>
  export const Level = ContentLevel

  // 마지막으로 채점한 page
  export const Page = NonZeroNullableNumber
  export type Page = S.Schema.To<typeof Page>
  export const PairX = nonZeroNullable(S.literal(1, 2, 3))
  export type PairX = S.Schema.To<typeof PairX>
  export const SignatureType = S.enums(SIGNATURE_TYPE)

  export const AssignedStudent = S.struct({
    id: S.number,
    studentName: S.string,
    studentStatus: StudentDomain.Status,
    workbookId: S.number,
    studentId: StudentDomain.Id,
  })
  export type AssignedStudent = S.Schema.From<typeof AssignedStudent>

  export const OptionalAssignedInfo = S.struct({
    assigned: S.boolean,
    list: S.array(AssignedStudent),
    lastAssignDateTime: ISODateString,
  })
}

export abstract class WorkbookCommon extends S.Class<WorkbookCommon>()({
  id: WorkbookDomain.Id,
  level: WorkbookDomain.Level,
  page: WorkbookDomain.Page, // 마지막으로 채점한 페이지
  schoolType: schoolType,
  grade: allGradeSubject,
  semester: S.nullable(semester), // grade가 과목인 경우 null
  title: S.string,
  fulltitle: S.string,
  assignmentFlag: S.union(S.boolean, S.undefined), // 출제가능여부 (출제 되었다가 아님)
  autoScored: S.boolean,
  coverType: S.nullable(S.number),
  conceptFlag: S.boolean, // 개념 여부
  createDatetime: ISODate,
  // 나중에 추가되어서 사용되어질 값?
  // lastUsePageNumber: S.nullable(S.number),
}) {
  abstract kind: ValueOf<typeof WorkbookDomain.TYPE>
}

// 시중교재
export class PublicWorkbook extends WorkbookCommon.extend<PublicWorkbook>()({
  subtitle: S.string,
  type: S.literal(WorkbookDomain.TYPE.시중교재),
  publisher: S.string,
  curriculumConceptType: S.enums(CURRICULUM_CONCEPT_TYPE),
  pairX: WorkbookDomain.PairX,
  pairFlag: S.boolean,
  /**
   * 교재 콘텐츠 확보 방식
   * - 시중교재가 아닌 경우 -> null
   * - 시중교재인 경우 -> 'PRIMARY' | 'SECONDARY'
   * - 시중교재는 교재 데이터를 어떤 방식으로 확보했는지에 따라 1군과 2군으로 분류함
   * - 1군: `지원해야하는 시중교재 콘팀이 판단 → 시중교재 구매 > 빠른정답 입력 > CMS 업로드`
   * - 2군: `교재 수급 → 스캔대행 → OCR 분석 → 유사문제 추출 → DB 등록`
   */
  securingType: S.enums(WorkbookDomain.SECURING_TYPE),
}) {}

// 교과서
export class SchoolWorkbook extends WorkbookCommon.extend<SchoolWorkbook>()({
  subtitle: S.string,
  type: S.literal(WorkbookDomain.TYPE.교과서),
  edition: S.string,
  publisher: S.string,
  teacherId: TeacherSchema.Id,
  pairX: WorkbookDomain.PairX,
  pairFlag: S.boolean,
}) {}

export class CustomWorkbook extends WorkbookCommon.extend<CustomWorkbook>()({
  type: S.literal(WorkbookDomain.TYPE.내교재),
  headtitle: S.string,
  subtitle: S.string,
  minPage: S.number,
  maxPage: S.number,
  answerPage: S.number,
  curriculumConceptType: S.enums(CURRICULUM_CONCEPT_TYPE),
  partitionType: S.number, // 교재 분할 타입
  pdfUrl: S.nullable(S.string),
  answerPdfUrl: S.nullable(S.string),
  academyId: AcademyDomain.Id,
  teacherId: TeacherSchema.Id,
  thumbnailImageUrl: S.string,
  originWorkbook: S.nullable(
    S.struct({
      id: S.number,
      fulltitle: S.string,
      securingType: S.enums(WorkbookDomain.SECURING_TYPE),
      type: S.enums(WorkbookDomain.TYPE),
    }),
  ),
}) {}
export class SignatureWorkbook extends WorkbookCommon.extend<CustomSignatureWorkbook>()({
  type: S.literal(WorkbookDomain.TYPE.시그니처교재),
  publisher: S.string,
  pdfUrl: S.string,
  pairX: WorkbookDomain.PairX,
  pairFlag: S.boolean,
  signatureType: WorkbookDomain.SignatureType,
  thumbnailImageUrl: S.string,
  signatureWorkbookFulltitle: S.string,
}) {}
export class CustomSignatureWorkbook extends WorkbookCommon.extend<CustomSignatureWorkbook>()({
  type: S.literal(WorkbookDomain.TYPE.커스텀시그니처교재),
  publisher: S.string,
  pdfUrl: S.string,
  pairX: WorkbookDomain.PairX,
  pairFlag: S.boolean,
  signatureType: WorkbookDomain.SignatureType,
  thumbnailImageUrl: S.string,
  signatureWorkbookFulltitle: S.string,
}) {}

export const Workbook = S.union(
  PublicWorkbook.struct,
  SchoolWorkbook.struct,
  CustomWorkbook.struct,
  CustomSignatureWorkbook.struct,
)

export type Workbook = S.Schema.To<typeof Workbook>
